import { QueryClient, UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import OCGatewayClient from '../client/OCGatewayClient';
import { PushNotificationConfig, PushNotificationConfigJson } from '../models/PushNotificationConfig';
import { gatewayUserScopedQueryOptions } from './gatewayQueryUtils';
import { queryClient as defaultQueryClient } from './queryClient';

export const pushNotificationConfigQueryKey = ['getPushNotificationConfig'] as const;

export async function getPushNotificationConfig(): Promise<PushNotificationConfig> {
    const response = await OCGatewayClient.request('getPushNotificationConfig', {});

    return new PushNotificationConfig(response as Record<string, any>);
}

export async function setPushNotificationConfig(
    config: PushNotificationConfigJson
): Promise<PushNotificationConfig> {
    const response = await OCGatewayClient.request('setPushNotificationConfig', { config });

    return new PushNotificationConfig(response as Record<string, any>);
}

export interface PushNotificationContext {
    locale: string;
    timezone: string;
}

export async function setPushNotificationContext(
    context: PushNotificationContext
): Promise<PushNotificationConfig> {
    const response = await OCGatewayClient.request('setPushNotificationContext', { context });

    return new PushNotificationConfig(response as Record<string, any>);
}

export function setCachedPushNotificationConfig(
    config: PushNotificationConfig | PushNotificationConfigJson,
    queryClient: QueryClient = defaultQueryClient
) {
    queryClient.setQueryData(pushNotificationConfigQueryKey, new PushNotificationConfig(config));
}

export function usePushNotificationConfigQuery(options?: UseQueryOptions<PushNotificationConfig, unknown>) {
    return useQuery({
        queryKey: pushNotificationConfigQueryKey,
        queryFn: getPushNotificationConfig,
        ...gatewayUserScopedQueryOptions,
        ...options
    });
}

export function useSetPushNotificationConfigMutation(
    options?: UseMutationOptions<PushNotificationConfig, unknown, PushNotificationConfigJson, { previousConfig?: PushNotificationConfig }>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: setPushNotificationConfig,
        ...options,
        onMutate: async (config, mutationContext) => {
            await queryClient.cancelQueries({ queryKey: pushNotificationConfigQueryKey });

            const previousConfig = queryClient.getQueryData<PushNotificationConfig>(pushNotificationConfigQueryKey);
            setCachedPushNotificationConfig(config, queryClient);

            await options?.onMutate?.(config, mutationContext);

            return { previousConfig };
        },
        onError: (error, config, onMutateResult, mutationContext) => {
            if (onMutateResult?.previousConfig) {
                setCachedPushNotificationConfig(onMutateResult.previousConfig, queryClient);
            }

            options?.onError?.(error, config, onMutateResult, mutationContext);
        },
        onSuccess: (config, variables, onMutateResult, mutationContext) => {
            setCachedPushNotificationConfig(config, queryClient);
            options?.onSuccess?.(config, variables, onMutateResult, mutationContext);
        },
        onSettled: (config, error, variables, onMutateResult, mutationContext) => {
            options?.onSettled?.(config, error, variables, onMutateResult, mutationContext);
        }
    });
}
